import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUpload, fetchAllPages, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

export const VEHICLE_TYPES = ['tractor_unit', 'dump_truck', 'semi_trailer', 'tuktuk_scooter'];
export const VEHICLE_STATUSES = ['active', 'in_service', 'decommissioned'];

// Must stay in sync with blueprints/vehicles.py's VEHICLE_TYPE_REQUIRED_CATEGORY
// — same "must not drift" reasoning as DRIVER_CHANNELS in useDriverAdmin.js.
// tuktuk_scooter is absent on purpose there (this fleet's one non-heavy
// type); mirrored here for the same reason.
export const VEHICLE_TYPE_REQUIRED_CATEGORY = {
  dump_truck: 'C',
  semi_trailer: 'CE',
  tractor_unit: 'CE',
};
export const DEFECT_SEVERITIES = ['low', 'medium', 'high', 'critical_grounded'];
export const DEFECT_STATUSES = ['open', 'in_repair', 'resolved'];

/**
 * The endpoint answers `{data, total_count}` (see core/paging.py). This hook
 * hands `data` back as `data` so every existing caller keeps working
 * untouched, and exposes the server's total separately as `totalCount` —
 * which is the number the compliance KPIs must read, not the length of
 * whichever page happens to be loaded.
 */
export function useVehicles(filters = {}) {
  return useVehicleQuery({ ...filters, all: false });
}

/**
 * Every vehicle, paged through to the end.
 *
 * The defect Kanban turns a vehicle_id into a licence plate with this, and
 * the defect form offers it as a picker. Both called useVehicles() before,
 * which returned the whole fleet until this endpoint paginated and then
 * silently returned its first 25 — so a defect on the 26th truck rendered
 * with no plate, and that truck could not be picked to report one against.
 */
export function useAllVehicles(filters = {}) {
  return useVehicleQuery({ ...filters, all: true });
}

function useVehicleQuery({ status, expiringWithinDays, search, limit, offset, includeInactive, all }) {
  const tenantId = useTenantId();
  const path = `/api/tenants/${tenantId}/vehicles`;
  const filters = {
    status,
    expiring_within_days: expiringWithinDays,
    search,
    // Archived vehicles are off by default and stay off for
    // useComplianceCounts() below — a truck nobody drives anymore must not
    // inflate the fleet total or the expiring-documents count.
    include_inactive: includeInactive ? '1' : undefined,
  };

  const query = useQuery({
    queryKey: qk.vehicles(tenantId, {
      status, expiringWithinDays, search, limit, offset, includeInactive, all,
    }),
    queryFn: () =>
      all
        ? fetchAllPages(path, filters)
        : api(`${path}${qs({ ...filters, limit, offset })}`),
    enabled: Boolean(tenantId),
    // The envelope, not a bare array: keeping the placeholder the same shape
    // as a real response is what stops every consumer needing its own
    // `?? []` for the first render.
    placeholderData: { data: [], total_count: 0 },
  });

  return { ...query, data: query.data?.data ?? [], totalCount: query.data?.total_count ?? 0 };
}

/**
 * The Transportas KPI row. Five filtered calls rather than counting
 * client-side off one: the expiry windows are a SQL date comparison against
 * the server's clock, and a browser with a wrong system date would
 * otherwise quietly report the wrong number of trucks about to fall out of
 * compliance — the one number on that screen nobody can afford to be wrong.
 *
 * Each of these reads only `total_count`, so it is five COUNT(*)s and five
 * throwaway pages of 25. That is strictly less data than the three
 * fetch-everything calls this replaced, for any fleet past about forty
 * vehicles — and unlike those, it stays right as the fleet grows.
 */
export function useComplianceCounts() {
  const tenantId = useTenantId();
  const all = useVehicles();
  const soon = useVehicles({ expiringWithinDays: 30 });
  const critical = useVehicles({ expiringWithinDays: 7 });
  // Counted by the server, not by measuring the arrays. These were
  // `.length` of the returned list, which was correct only for as long as
  // the endpoint returned every row — the moment the table paginated, "how
  // many trucks do we have" would have started answering 25.
  const activeOnly = useVehicles({ status: 'active' });
  const inServiceOnly = useVehicles({ status: 'in_service' });
  return {
    isLoading: all.isLoading || soon.isLoading || critical.isLoading,
    total: all.totalCount,
    active: activeOnly.totalCount,
    inService: inServiceOnly.totalCount,
    expiring30: soon.totalCount,
    expiring7: critical.totalCount,
    tenantId,
  };
}

/** `status` omitted returns every defect — what the Kanban needs, since it
 *  renders all three columns. Pass 'open' for the KPI card's count. */
export function useDefects(status) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.defects(tenantId, status),
    queryFn: () => api(`/api/tenants/${tenantId}/vehicle-defects${qs({ status })}`),
    enabled: Boolean(tenantId),
    placeholderData: [],
  });
}

function useFleetInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['vehicles', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['vehicle-defects', tenantId] });
  };
}

export function useSaveVehicle() {
  const tenantId = useTenantId();
  const invalidate = useFleetInvalidation();
  return useMutation({
    mutationFn: ({ vehicleId, ...payload }) =>
      vehicleId
        ? api(`/api/tenants/${tenantId}/vehicles/${vehicleId}`, { method: 'PUT', body: payload })
        : api(`/api/tenants/${tenantId}/vehicles`, { method: 'POST', body: payload }),
    onSuccess: invalidate,
  });
}

/** Soft delete — the backend keeps the row (assignment history, defects and
 *  order references stay intact) and drops it out of every list. */
export function useDeleteVehicle() {
  const tenantId = useTenantId();
  const invalidate = useFleetInvalidation();
  return useMutation({
    mutationFn: (vehicleId) =>
      api(`/api/tenants/${tenantId}/vehicles/${vehicleId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/**
 * Un-archives a vehicle. Until this existed there was no way back at all:
 * DELETE set soft_deleted=1 and every other route refused to load the row,
 * so a mis-clicked "Šalinti" could only be undone with hand-written SQL
 * against the database. The backend accepts this as a request of its own —
 * restore first, edit after.
 */
export function useRestoreVehicle() {
  const tenantId = useTenantId();
  const invalidate = useFleetInvalidation();
  return useMutation({
    mutationFn: (vehicleId) =>
      api(`/api/tenants/${tenantId}/vehicles/${vehicleId}`, {
        method: 'PUT',
        body: { soft_deleted: false },
      }),
    onSuccess: invalidate,
  });
}

/**
 * Dispatcher-side defect reporting — the same endpoint the driver app posts
 * to, so a fault phoned in to the office and one reported from the cab land
 * as the same record with the same side effects (a critical_grounded defect
 * takes the truck off the road either way).
 *
 * Multipart rather than JSON unconditionally: the photo is optional, but
 * branching on it would mean two request shapes to keep in step with the
 * route's form/JSON split for no gain.
 */
export function useReportDefect() {
  const invalidate = useFleetInvalidation();
  return useMutation({
    mutationFn: ({ vehicleId, title, severity, description, photo }) => {
      const form = new FormData();
      form.append('title', title);
      form.append('severity', severity);
      if (description) form.append('description', description);
      if (photo) form.append('photo', photo);
      return apiUpload(`/api/vehicles/${vehicleId}/defects`, form);
    },
    onSuccess: invalidate,
  });
}

export function useAssignVehicleDriver() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const invalidate = useFleetInvalidation();
  return useMutation({
    mutationFn: ({ vehicleId, driverId }) =>
      api(`/api/tenants/${tenantId}/vehicles/${vehicleId}/assign-driver`, {
        method: 'POST',
        body: { driver_id: driverId || null },
      }),
    onSuccess: () => {
      invalidate();
      // V-284. blueprints/drivers.py's list_drivers() resolves license_plate
      // live off vehicles.current_driver_id (V-225/P0-5) — exactly the
      // pointer this endpoint moves. Without this, the Drivers panel keeps
      // showing both drivers on their pre-reassignment trucks (the old
      // holder AND the new one) for up to staleTime, since 'drivers' was
      // never in useFleetInvalidation()'s set at all.
      queryClient.invalidateQueries({ queryKey: ['drivers', tenantId] });
    },
  });
}

/** Kanban column moves: open -> in_repair -> resolved, and back. */
export function useMoveDefect() {
  const invalidate = useFleetInvalidation();
  return useMutation({
    mutationFn: ({ defectId, status }) =>
      api(`/api/vehicle-defects/${defectId}`, { method: 'PUT', body: { status } }),
    onSuccess: invalidate,
  });
}
